/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, Combobox, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { useWorkItemFieldHook } from './use-work-item-field';
import { useAzureProvider } from 'src/azure/context';
import { ConditionItemType, FieldMeetCertainCriteriaType } from '../../types';
import { useAutomationRuleConditions } from '../../use-conditions';
import { fieldMeetCertainCriteriaStyles } from '../styles';
export interface Props {
  conditionId: string;
  validationError: string | null;
	initialOptions?: Array<{ label: string; value: string }>;
	onChangeWorkItemField(fieldName: string): void;
}

export default function WorkItemFieldInput(props: Props) {
	const classes = fieldMeetCertainCriteriaStyles();
	const {applicationContext} = useAzureProvider();
	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const {conditions, setConditions, resetValidationErrors} = useAutomationRuleConditions();

	const workItemFieldHook = useWorkItemFieldHook();

	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: FieldMeetCertainCriteriaType = useMemo(() => {
		const foundConditions = conditions.find(item => item.id === props.conditionId) as ConditionItemType;
		return foundConditions!.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType;
	}, [conditions, props.conditionId]);
  
	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
	
		if (value === '') {
			workItemFieldHook
				.getWorkItemFieldOptions({
					projectId: applicationContext.projectId,
				})
				.then((resultOptions) => {
					setOptions(resultOptions);
				});
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options, applicationContext.projectId]);

	const setWorkItemField = useCallback((value: string) => {
		resetValidationErrors();
		if (value) {
			setStatus('selected');
			const foundCondition = conditions.find(item => item.id === props.conditionId);
	
			if (foundCondition) {
				props.onChangeWorkItemField(value);
				setConditions(oldConditions => {
					const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
					const others = oldConditions.filter(item => item.id !== props.conditionId);
	
					return [...others, {
						...current, fieldMeetCertainCriteria: {
							...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
							operator: '',
							workItemField: value,
							userFieldValue: [],
							dateTimeValue: {
								type: 'datetime',
								datetime: {
									value: new Date(),
								},
								timeframe: {
									timeNumber: 0,
									timeUnit: 'minutes',
									timeOption: 'ago',
								},
							}
						}
					}];
				});
			}
		}
	}, []);

	const loadOptions = useCallback(async () => {
		const resultOptions = await workItemFieldHook.getWorkItemFieldOptions({projectId: applicationContext.projectId});
		setOptions(resultOptions);
	}, []); 

	const valueRender = useMemo(() => {
		if (current && current.workItemField && status === 'selected') {
			return options.find(item => item.value === current.workItemField)?.label || '';
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current.workItemField, options, status]);

	const selectedOptions = useMemo(() => {
		const found = options.find(item => item.value === current.workItemField);

		if (current.workItemField && found) {
			return [found.value];
		}

		return [];
	}, [current.workItemField, options]);

	useEffect(() => {
		setOnInitialLoading(true);
		const foundCondition = conditions.find(item => item.id === props.conditionId);

		if (
			foundCondition && 
			foundCondition?.fieldMeetCertainCriteria &&
			props.initialOptions
		) {
			// loadOptions();
			setOptions(props.initialOptions);
		}

		setOnInitialLoading(false);
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}

	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				id={`condition-${props.conditionId}-field-name`}
				className={classes.dropdown}
				autoComplete='off'
				appearance='outline'
				placeholder='Choose a field'
				positioning='below-start'
				clearable
				value={valueRender}
				selectedOptions={selectedOptions}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onOptionSelect={(_, data) => {
					setWorkItemField(data.optionValue as string);
				}}
				onBlur={() => {
					if (props.initialOptions) {
						setOptions(props.initialOptions);
						setStatus('selected');
					}

					if (!props.initialOptions) {
						loadOptions()
							.then(() => {
								setStatus('selected');
							});
					}
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
			</Combobox>
		</Field>
	);
}